import React, { useState, useEffect } from "react";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Bir kategoriyi kendisinin veya çocuklarının altına eklemeyi engellemek için filtreleme
const filterSelfAndChildren = (categories, categoryId) => {
  let forbiddenIds = [parseInt(categoryId)];
  let idsToProcess = [parseInt(categoryId)];

  while (idsToProcess.length > 0) {
    const currentParentId = idsToProcess.shift();
    const children = categories.filter(
      (cat) => cat.parent_id === currentParentId
    );
    for (const child of children) {
      forbiddenIds.push(child.id);
      idsToProcess.push(child.id);
    }
  }
  return categories.filter((cat) => !forbiddenIds.includes(cat.id));
};

export default function AdminCategoryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [categoryData, setCategoryData] = useState({
    name_tm: "",
    name_ru: "",
    name_en: "",
    parent_id: "",
    order_number: 0,
    just_category: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [formattedCategories, setFormattedCategories] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubcategories, setHasSubcategories] = useState(false);
  const [isSubcategory, setIsSubcategory] = useState(false); // Alt kategori mi kontrolü

  useEffect(() => {
    const fetchCategoryData = async () => {
      setLoading(true);
      try {
        const [categoryToEditRes, allCategoriesRes, subcategoriesRes] = await Promise.all([
          axios.get(`${CONSTANTS.baseRoute}/categories/${id}`),
          axios.get(`${CONSTANTS.baseRoute}/categories`),
          axios.get(`${CONSTANTS.baseRoute}/categories/${id}/has-subcategories`),
        ]);

        const categoryToEdit = categoryToEditRes.data;
        const allCats = allCategoriesRes.data;
        
        setAllCategories(allCats);
        
        // Alt kategori mi kontrol et (parent_id varsa)
        setIsSubcategory(!!categoryToEdit.parent_id);

        setCategoryData({
          name_tm: categoryToEdit.name_tm || "",
          name_ru: categoryToEdit.name_ru || "",
          name_en: categoryToEdit.name_en || "",
          parent_id: categoryToEdit.parent_id || "",
          order_number: categoryToEdit.order_number || 0,
          just_category: categoryToEdit.just_category || false,
        });

        setHasSubcategories(subcategoriesRes.data.hasSubcategories);

        if (categoryToEdit.image_path) {
          setImagePreview(`${CONSTANTS.baseRoute}${categoryToEdit.image_path}`);
        }

        // Sadece just_category = false olan kategorileri filtrele
        const validCategories = allCats.filter(cat => !cat.just_category);
        const filteredList = filterSelfAndChildren(validCategories, id);
        const rootCategories = filteredList.filter((cat) => !cat.parent_id);
        rootCategories.sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
        setFormattedCategories(rootCategories);
      } catch (error) {
        console.error("Bilgiler çekilirken hata:", error);
        setMessage({ 
          type: "danger", 
          text: t("adminCategoryEdit.loadError") 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [id, t]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Eğer just_category checkbox'ı değiştiyse
    if (name === "just_category") {
      setCategoryData((prev) => ({
        ...prev,
        just_category: checked,
        // just_category true olduğunda parent_id'yi temizle
        parent_id: checked ? "" : prev.parent_id,
      }));
    } else {
      setCategoryData((prev) => ({ 
        ...prev, 
        [name]: type === "checkbox" ? checked : (name === "order_number" ? Number(value) || 0 : value)
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({ 
          type: "danger", 
          text: t("adminCategoryAdd.onlyImagesAllowed") 
        });
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMessage({ type: "", text: "" });

    // Validation
    if (!categoryData.name_tm.trim()) {
      setMessage({ 
        type: "danger", 
        text: t("adminCategoryAdd.nameTmRequired") 
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!categoryData.parent_id && !imagePreview && !imageFile) {
      setMessage({ 
        type: "danger", 
        text: t("adminCategoryEdit.mainCategoryImageRequired") 
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Alt kategorisi varsa just_category false olmalı
    if (hasSubcategories && categoryData.just_category) {
      setMessage({ 
        type: "danger", 
        text: t("adminCategoryEdit.cannotBeJustCategoryWithSubcategories") 
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Alt kategori ise just_category true olamaz
    if (isSubcategory && categoryData.just_category) {
      setMessage({ 
        type: "danger", 
        text: t("adminCategoryEdit.subcategoryCannotBeJustCategory") 
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // just_category true ise parent_id boş olmalı
    if (categoryData.just_category && categoryData.parent_id) {
      setMessage({ 
        type: "danger", 
        text: t("adminCategoryEdit.justCategoryCannotHaveParent") 
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Eğer parent_id seçilmişse ve seçilen kategori just_category ise kontrol et
    if (categoryData.parent_id) {
      const selectedParent = allCategories.find(cat => cat.id === parseInt(categoryData.parent_id));
      if (selectedParent && selectedParent.just_category) {
        setMessage({ 
          type: "danger", 
          text: t("adminCategoryEdit.cannotAddToJustCategory") 
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name_tm", categoryData.name_tm.trim());
    formData.append("name_ru", categoryData.name_ru.trim() || "");
    formData.append("name_en", categoryData.name_en.trim() || "");
    
    // just_category true ise parent_id gönderme
    if (categoryData.parent_id && !categoryData.just_category) {
      formData.append("parent_id", categoryData.parent_id);
    } else {
      formData.append("parent_id", "");
    }
    
    formData.append("order_number", categoryData.order_number);
    formData.append("just_category", categoryData.just_category ? "1" : "0");

    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      await axios.put(
        `${CONSTANTS.baseRoute}/categories/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessage({ 
        type: "success", 
        text: t("adminCategoryEdit.updateSuccess") 
      });
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      const errMsg = 
        error.response?.data?.message || 
        t("adminCategoryEdit.updateError");
      setMessage({ type: "danger", text: errMsg });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-main p-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="admin-main ps-2 pe-3 pb-5">
      <div className="d-flex gap-2 align-items-center my-3">
        <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn admin-icon-btn" onClick={() => navigate(1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold mb-0">{t("adminCategoryEdit.title")}</h5>
      </div>

      {message.text && (
        <div 
          className={`alert alert-${message.type} shadow-sm mb-4 sticky-top`} 
          style={{ top: "70px", zIndex: 1000 }}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="row g-4">
          {/* Resim Bölümü */}
          <div className="col-lg-4">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title mb-3 fs-6 fw-bold">
                  {t("adminCategoryAdd.imageTitle")} <span className="text-danger">*</span>
                </h5>
                
                <input
                  type="file"
                  id="imageUpload"
                  className="d-none"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                <label
                  htmlFor="imageUpload"
                  className={`image-uploader-box flex-grow-1 border rounded d-flex align-items-center justify-content-center overflow-hidden cursor-pointer ${
                    !imagePreview ? 'bg-light border-dashed' : ''
                  }`}
                  style={{ minHeight: "280px" }}
                >
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt={t("adminCategoryAdd.preview")} 
                      className="img-fluid w-100 h-100" 
                      style={{ objectFit: "contain" }} 
                    />
                  ) : (
                    <div className="text-center p-4">
                      <i className="bi bi-cloud-arrow-up fs-2 text-muted mb-3 d-block"></i>
                      <div className="text-muted mb-1">
                        {t("adminCategoryAdd.dragOrClick")}
                      </div>
                      <small className="text-secondary">
                        {t("adminCategoryAdd.supportedFormats")}
                      </small>
                    </div>
                  )}
                </label>

                {!categoryData.parent_id && !imagePreview && !imageFile && (
                  <div className="text-danger small mt-2">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {t("adminCategoryEdit.mainCategoryImageRequired")}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Bölümü */}
          <div className="col-lg-8">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-4 fs-6 fw-bold">
                  {t("adminCategoryAdd.categoryInfo")}
                </h5>

                <div className="mb-4">
                  <label htmlFor="name_tm" className="form-label fw-semibold small">
                    {t("adminCategoryAdd.nameTm")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name_tm"
                    name="name_tm"
                    value={categoryData.name_tm}
                    onChange={handleChange}
                    placeholder={t("adminCategoryAdd.nameTmPlaceholder")}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="name_ru" className="form-label fw-semibold small">
                    {t("adminCategoryAdd.nameRu")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name_ru"
                    name="name_ru"
                    value={categoryData.name_ru}
                    onChange={handleChange}
                    placeholder={t("adminCategoryAdd.nameRuPlaceholder")}
                  />
                </div>

                <div className="row g-3">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="parent_id" className="form-label fw-semibold small">
                      {t("adminCategoryAdd.parentCategory")}
                    </label>
                    <select
                      className="form-select"
                      id="parent_id"
                      name="parent_id"
                      value={categoryData.parent_id}
                      onChange={handleChange}
                      disabled={categoryData.just_category} // just_category true iken select disabled
                    >
                      <option value="">
                        {t("adminCategoryAdd.noParent")}
                      </option>
                      {/* just_category false iken diğer seçenekleri göster */}
                      {!categoryData.just_category && formattedCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name_tm}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted d-block mt-1">
                      {categoryData.just_category 
                        ? t("adminCategoryEdit.justCategoryNoParent") 
                        : t("adminCategoryAdd.onlyNonJustCategories")}
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="order_number" className="form-label fw-semibold small">
                      {t("adminCategoryAdd.orderNumber")}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="order_number"
                      name="order_number"
                      value={categoryData.order_number}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Just Category Checkbox - Sadece ana kategoriler için göster */}
                {!isSubcategory ? (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="just_category"
                            name="just_category"
                            checked={categoryData.just_category}
                            onChange={handleChange}
                            style={{ cursor: "pointer", width: "40px", height: "20px" }}
                            disabled={hasSubcategories}
                          />
                          <label 
                            className="form-check-label ms-2 fw-semibold" 
                            htmlFor="just_category"
                            style={{ cursor: hasSubcategories ? "not-allowed" : "pointer" }}
                          >
                            {t("justcategory")}
                          </label>
                        </div>
                       
                      </div>
                      <small className="text-muted d-block mt-1">
                        {categoryData.just_category 
                          ? t("adminCategoryEdit.justCategoryInfo") 
                          : t("adminCategoryEdit.justCategoryHelp")}
                      </small>
                    </div>
                  </div>
                ) : (
                  // Alt kategoriler için bilgi mesajı
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="alert alert-secondary py-2 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        {t("adminCategoryEdit.subcategoryJustCategoryInfo")}
                      </div>
                    </div>
                  </div>
                )}

                {categoryData.just_category && !isSubcategory && (
                  <div className="alert alert-info mt-3 py-2">
                    <i className="bi bi-info-circle me-2"></i>
                    {t("adminCategoryEdit.justCategoryActiveInfo")}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-5">
                  <button 
                    type="submit" 
                    className="btn btn-primary px-5 py-2 fw-bold shadow-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2-circle me-1"></i>
                        {t("common.save")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}